import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Table,
  Button,
  Modal,
  Input,
  Select,
  message,
  Popconfirm,
  Card,
  Space,
  Badge,
  Typography,
  Row,
  Col,
  Spin,
  DatePicker,
  InputNumber,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  FlagOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import instance from '@/services/axios.config';
import InspectionBtn from '@/components/InspectionBtn';
import { useDispatch, useSelector } from 'react-redux';
import toast from "react-hot-toast";
import { setSubscription } from '@/stores/slice/authSlice';

const { Text } = Typography;
const { Option } = Select;

const OwnerShipsManagement = ({ sharedContext, subscription }) => {
  const { ships, vesselTypes, loading, loadShips } = sharedContext;
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingShip, setEditingShip] = useState(null);
  const dispatch = useDispatch()
  // React Hook Form setup
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm({
    defaultValues: {
      name: '',
      imo_number: '',
      vessel_type: '',
      flag: '',
      deadweight: null,
      year_of_build: null,
      length: null,
      beam: null,
      engine_type: '',
      gross_tonnage: '',
      engine_modal: '',
      engine_power: ''
    }
  });

  // Handle Add Ship
  const handleAddShip = () => {
    setIsEditMode(false);
    setEditingShip(null);
    reset({
      name: '',
      imo_number: '',
      vessel_type: '',
      flag: '',
      deadweight: null,
      year_of_build: null,
      engine_type: '',
      gross_tonnage: '',
      engine_modal: '',
      engine_power: ''
    });
    setIsModalVisible(true);
  };

  // Handle Edit Ship
  const handleEditShip = (ship) => {
    setIsEditMode(true);
    setEditingShip(ship);
     // Set form values for editing
    reset({
      name: ship.name || '',
      imo_number: ship.imo_number || '',
      vessel_type: ship.vessel_type || '',
      flag: ship.flag || '',
      deadweight: ship.deadweight || null,
      year_of_build: ship.year_of_build ? dayjs().year(ship.year_of_build) : null,
      engine_type: ship.engine_type || '',
      gross_tonnage: ship.gross_tonnage || '',
      engine_model: ship.engine_model || '',
      engine_power: ship.engine_power || ''
    });

    setIsModalVisible(true);
  };

   // Handle Form Submit
  const onSubmit = async (values) => {
    setSubmitLoading(true);
    try {
      const shipData = {
        ...values,
      };
      let subscriptionpayload = subscription

      if (isEditMode && editingShip) {
        // Update ship
        await instance.put(`/ships/${shipData.imo_number}/`, shipData);
        toast.success('Ship updated successfully');
      } else {
        // Create ship
        await instance.post('/ships', values);
        subscriptionpayload = { ...subscriptionpayload, max_ships: subscriptionpayload.max_ships - 1 };
         dispatch(setSubscription(subscriptionpayload))
        subscription
        toast.success('Ship created successfully');
      }

      setIsModalVisible(false);
      reset();
      await loadShips(); // Reload ships using shared function
    } catch (error) {
      toast.error(error.message || 'Operation failed');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Handle Delete Ship
  const handleDeleteShip = async (shipId) => {
    try {
      await instance.put(`/ships/${shipId}`, {});
      toast.success('Ship deleted successfully');
      await loadShips(); // Reload ships using shared function
    } catch (error) {
      toast.error(error.message || 'Failed to delete ship');
    }
  };

  // Handle Cancel Modal
  const handleCancel = () => {
    setIsModalVisible(false);
    reset();
    setEditingShip(null);
  };

  // Custom error component for form fields
  const ErrorMessage = ({ error }) => {
    if (!error) return null;
    return <div className="text-red-500 text-sm mt-1">{error.message}</div>;
  };

  // Table columns definition
  const columns = [
    {
      title: 'Ship Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div className="flex items-center gap-2">
          <div>
            <div className="font-medium text-gray-800">{text}</div>
            <div className="text-sm text-gray-500">{record.deadweight} DWT</div>
          </div>
          {!record.synced && (
            <Badge
              status="warning"
              title="Pending sync"
              className="ml-2"
            />
          )}
        </div>
      ),
    },
    {
      title: 'IMO Number',
      dataIndex: 'imo_number',
      key: 'imo_number',
      render: (text) => (
        <span className="font-mono text-sm">{text}</span>
      )
    },
    {
      title: 'Type',
      dataIndex: 'vessel_type',
      key: 'vessel_type',
      render: (text) => (
        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
          {text}
        </span>
      )
    },
    {
      title: 'Flag',
      dataIndex: 'flag',
      key: 'flag',
      render: (text) => (
        <span className="flex items-center">
          <FlagOutlined className="mr-1 text-gray-500" />
          {text}
        </span>
      )
    },
    {
      title: 'Year Built',
      dataIndex: 'year_of_build',
      key: 'year_of_build',
      render: (text) => (
        <span className="flex items-center">
          <CalendarOutlined className="mr-1 text-gray-500" />
          {text}
        </span>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit Ship">
            <Button
              icon={<EditOutlined />}
              onClick={() => handleEditShip(record)}
              className="text-blue-600 hover:text-blue-900"
              size="small"
              type="text"
            />
          </Tooltip>
          <Popconfirm
            title="Delete Ship"
            description="Are you sure you want to delete this ship?"
            onConfirm={() => handleDeleteShip(record.imo_number)}
            okText="Yes"
            cancelText="No"
            okType="danger"
          >
            <Tooltip title="Delete Ship">
              <Button
                danger
                icon={<DeleteOutlined />}
                className="text-red-600 hover:text-red-900"
                size="small"
                type="text"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];
  console.log("subscription?.max_ships?.length",)

  return (
    <div>
      {/* Ships Management Section */}
      <div className="flex justify-between items-center bg-white rounded-lg shadow-sm p-2 mb-3">

        <Typography.Title level={5} className="mb-0 text-gray-800">
          Vessels ({ships?.length})
        </Typography.Title>
        <InspectionBtn
          type='primary' size='small'
          disabled={subscription?.max_ships <= 0 ? true : false}
          className="bg-blue-600 hover:bg-blue-700 rounded-xl"
          onClick={handleAddShip}
          icon={<PlusOutlined />}
        >
          Add Ship
        </InspectionBtn>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <Table
          columns={columns}
          className="spotflash-table"

          dataSource={ships}
          rowKey="id"
          loading={loading}
          scroll={{ x: 'max-content' }} // 👈 adds horizontal scroll on small screens

          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} ships`,
          }}
          rowClassName={(record, index) =>
            index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
          }
        />
      </div>

      {/* Ship Form Modal */}
      <Modal
        title={
          <div className="flex items-center text-lg">
            {isEditMode ? 'Edit Ship' : 'Add New Ship'}
          </div>
        }
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={800}
        className="top-10"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="mt-4">
          <Row gutter={16}>
            <Col span={12}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ship Name *
                </label>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: 'Please enter ship name' }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Enter ship name"
                      size="large"
                      status={errors.name ? 'error' : ''}
                    />
                  )}
                />
                <ErrorMessage error={errors.name} />
              </div>
            </Col>
            <Col span={12}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  IMO Number *
                </label>
                <Controller
                  name="imo_number"
                  control={control}
                  rules={{ required: 'Please enter IMO number' }}
                  render={({ field }) => (
                    <InputNumber
                      style={{ width: '100%' }}
                      {...field}
                      placeholder="e.g., 1234567"
                      size="large"
                      status={errors.imo_number ? 'error' : ''}
                    />
                  )}
                />
                <ErrorMessage error={errors.imo_number} />
              </div>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vessel Type *
                </label>
                <Controller
                  name="vessel_type"
                  control={control}
                  rules={{ required: 'Please select vessel type' }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder="Select vessel type"
                      size="large"
                      style={{ width: '100%' }}
                      status={errors.vessel_type ? 'error' : ''}
                    >
                      {vesselTypes.map(type => (
                        <Option key={type} value={type}>
                          {type}
                        </Option>
                      ))}
                    </Select>
                  )}
                />
                <ErrorMessage error={errors.vessel_type} />
              </div>
            </Col>
            <Col span={12}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Flag *
                </label>
                <Controller
                  name="flag"
                  control={control}
                  rules={{ required: 'Please enter flag' }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      prefix={<FlagOutlined />}
                      placeholder="Enter flag country"
                      size="large"
                      status={errors.flag ? 'error' : ''}
                    />
                  )}
                />
                <ErrorMessage error={errors.flag} />
              </div>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deadweight (DWT) *
                </label>
                <Controller
                  name="deadweight"
                  control={control}
                  rules={{ required: 'Please enter deadweight' }}
                  render={({ field }) => (
                    <InputNumber
                      {...field}
                      placeholder="Enter deadweight"
                      size="large"
                      style={{ width: '100%' }}
                      min={0}
                      formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      status={errors.deadweight ? 'error' : ''}
                    />
                  )}
                />
                <ErrorMessage error={errors.deadweight} />
              </div>
            </Col>
            <Col span={12}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Year Built *
                </label>
                <Controller
                  name="year_of_build"
                  control={control}
                  rules={{ required: 'Please select year built' }}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      picker="year"
                      placeholder="Select year"
                      size="large"
                      style={{ width: '100%' }}
                      status={errors.year_of_build ? 'error' : ''}
                    />
                  )}
                />
                <ErrorMessage error={errors.year_of_build} />
              </div>
            </Col>
          </Row>

        
          <Row gutter={16}>
            <Col span={12}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gross Tonnage
                </label>
                <Controller
                  name="gross_tonnage"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Enter gross tonnage"
                      size="large"
                    />
                  )}
                />
              </div>
            </Col>
            <Col span={12}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Engine Type
                </label>
                <Controller
                  name="engine_type"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Enter engine type"
                      size="large"
                    />
                  )}
                />
              </div>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Engine Model
                </label>
                <Controller
                  name="engine_model"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Enter engine model"
                      size="large"
                    />
                  )}
                />
              </div>
            </Col>
            <Col span={12}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Engine Power
                </label>
                <Controller
                  name="engine_power"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Enter engine power"
                      size="large"
                    />
                  )}
                />
              </div>
            </Col>
          </Row>

          <div className="flex justify-end space-x-2 mt-6 pt-4 border-t">
            <InspectionBtn variant="secondary" size="large" onClick={handleCancel}>
              Cancel
            </InspectionBtn>
            <InspectionBtn
              type="primary"
              htmlType="submit"
              size="large"
              loading={submitLoading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isEditMode ? 'Update Ship' : 'Create Ship'}
            </InspectionBtn>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default OwnerShipsManagement;